import { type AirplaneInit, hooks } from "@/store.js";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import showErrMessage from "@/utils/showErrMessage.js";
import {
	Autocomplete,
	AutocompleteItem,
	Button,
	Checkbox,
	Form,
	Input,
	Popover,
	PopoverContent,
	PopoverTrigger,
	Select,
	SelectItem,
	Table,
	TableBody,
	TableCell,
	TableColumn,
	TableHeader,
	TableRow,
} from "@heroui/react";
import { useMemoizedFn, useRequest, useUpdateEffect } from "ahooks";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import isEqual from "react-fast-compare";
import { toast } from "sonner";
import { Drawer } from "vaul";
import { ModalProvider } from "react-modal-hook";

import { planDetailEvent } from "@/pages/PlanDetail/utils/event.js";

import SheetWrapper, {
	SheetWrapperHeader,
} from "@/components/SheetWrapperV2/index.jsx";

import VerdantProvider from "@/components/VerdantProvider.jsx";
import dayjs from "dayjs";
import {
	IonPicker,
	IonPickerColumn,
	IonPickerColumnOption,
} from "@ionic/react";
import axios from "axios";

interface IProps {
	date?: string;
	location?: string;
	defaultValue?: string;
	planId: string;
	onSuccess?: () => void;
}

const WEATHER = ["☀️", "⛅️", "☁️", "🌧"];

const Inner = memo(
	({ onSuccess, defaultValue, date, location, planId }: IProps) => {
		const [weather, setWeather] = useState(
			() => WEATHER.find((i) => defaultValue?.includes(i)) || "☀️",
		);

		const [startDegree, setStartDegree] = useState(() => {
			const res = defaultValue
				?.replace(weather, "")
				.split("~")[0]
				?.replace("°", "");

			return res ? Number(res) : 10;
		});
		const [endDegree, setEndDegree] = useState(() => {
			const res = defaultValue
				?.replace(weather, "")
				.split("~")[1]
				?.replace("°", "");

			return res ? Number(res) : 16;
		});

		const client = hooks.useClient();

		const submit = useMemoizedFn(async () => {
			const value = `${weather}${startDegree}°~${endDegree}°`;

			if (!date) return;
			if (!value) return;

			// STEP: 找到日期
			const entity = await client.weather.findOne({
				index: {
					where: "planId_date_location",
					match: { date, location, planId },
				},
			}).resolved;

			if (entity) {
				entity.set("temperature", value);
			} else {
				await client.weather.put({
					date,
					temperature: value,
					location,
					planId,
				});
			}

			toast.success("修改成功");
			setCloseKey(+new Date());
			onSuccess?.();
		});

		const [closeKey, setCloseKey] = useState(0);

		const { data: weatherAllData } = useRequest(
			async () => {
				const res = await axios.get<{
					jma: Array<{
						date: string;
						maxDegree: 0;
						minDegree: 0;
						precipitation: 0;
						weather: string;
					}>;
					tenki: Array<{
						date: string;
						maxDegree: 0;
						minDegree: 0;
						precipitation: 0;
						weather: string;
					}>;
					apple: Array<{
						date: string;
						maxDegree: 0;
						minDegree: 0;
						precipitation: 0;
						weather: string;
					}>;
				}>("https://1257170569-lx6oy5m0n3.ap-singapore.tencentscf.com");

				return res.data;
			},
			{
				cacheKey: "tenki-kyoto-all",
				staleTime: 5 * 60 * 1000,
				onError: showErrMessage,
			},
		);

		const weatherData = useMemo(
			() => [
				...(weatherAllData?.jma
					.filter((i) => i.date === date)
					.map((i) => ({ ...i, source: "jma" })) || []),
				...(weatherAllData?.tenki
					.filter((i) => i.date === date)
					.map((i) => ({
						...i,
						source: "tenki",
					})) || []),
				...(weatherAllData?.apple
					.filter((i) => i.date === date)
					.map((i) => ({
						...i,
						source: "apple",
					})) || []),
			],
			[weatherAllData, date],
		);

		return (
			<SheetWrapper closeKey={closeKey}>
				<Form
					className="w-full !block pb-safe -mt-3"
					onSubmit={async (e) => {
						e.stopPropagation();
						e.preventDefault();

						submit();
					}}
				>
					<SheetWrapperHeader title="编辑天气" align="left" okText="保存" />

					<div
						className="px-4 pt-4 pb-safe overflow-scroll"
						style={{ height: "calc(100vh - 380px)" }}
					>
						<header className="text-gray-800 text-3xl mb-4">
							<b>{dayjs(date).format("MM月DD日")} • 京都</b>
						</header>
						<div className="mb-4 flex items-center">
							{WEATHER.map((i) => {
								const isActive = weather === i;
								return (
									<span
										key={i}
										className={`text-4xl leading-none  w-16 h-16 flex items-center  justify-center mr-2 rounded-full  ${
											isActive ? "bg-gray-100" : ""
										}`}
										onClick={() => {
											setWeather(i);
										}}
									>
										{i}
									</span>
								);
							})}
						</div>
						<div className="flex border border-gray-200 rounded-2xl overflow-hidden">
							<IonPicker>
								<IonPickerColumn
									value={startDegree}
									onIonChange={(e) => {
										setStartDegree(Number(e.target.value));
									}}
								>
									{new Array(40)
										.fill(undefined)
										.map((i, index) => index + 1)
										.map((i) => (
											<IonPickerColumnOption key={i} value={i}>
												{i}
											</IonPickerColumnOption>
										))}
									<div slot="suffix">°</div>
								</IonPickerColumn>
							</IonPicker>

							<div className=" shrink-0 border-r border-gray-200" />
							<IonPicker>
								<IonPickerColumn
									value={endDegree}
									onIonChange={(e) => {
										setEndDegree(Number(e.target.value));
									}}
								>
									{new Array(40)
										.fill(undefined)
										.map((i, index) => index + 1)
										.map((i) => (
											<IonPickerColumnOption key={i} value={i}>
												{i}
											</IonPickerColumnOption>
										))}
									<div slot="suffix">°</div>
								</IonPickerColumn>
							</IonPicker>
						</div>
						{weatherData?.length ? (
							<span className="block text-gray-400 mt-4 font-semibold text-sm">
								参考天气
							</span>
						) : null}

						{weatherData?.map((i) => (
							<article
								key={i.source}
								className="text-gray-600 py-2 flex items-center"
							>
								{i.weather}
								{i.minDegree}°~{i.maxDegree}° {i.precipitation}%{" "}
								<span className="flex-grow" />
								<span className=" opacity-50">{i.source}</span>
							</article>
						))}
					</div>
				</Form>
			</SheetWrapper>
		);
	},
	isEqual,
);

const Modal = NiceModal.create((props: IProps) => {
	return (
		<VerdantProvider>
			<ModalProvider>
				<Inner {...props} />
			</ModalProvider>
		</VerdantProvider>
	);
});

export const UseSetWeather = () => {
	const modal = useModal(Modal);
	return {
		show: modal.show,
	};
};
